import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { Link, useParams } from 'react-router-dom';
import { useQueryState, parseAsString } from 'nuqs';
import { ArrowUpLeftFromSquare, HardDrive } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CamerasTab } from './tabs/CamerasTab';
import { DeviceFormProvider } from './DeviceFormProvider';
import { FetchDeviceQuery } from '@/lib/api/devices/devices';
import NotFoundPage from '@/app/ErrorHandling/NotFoundPage';
import { PageTitle } from '@/components/PageTitle';
import { DeviceSubmitButton } from './DeviceSubmit';
import { ConnectionsAndSettingsTab } from './tabs/ConnectionsAndSettingsTab';
import { ExternalDeviceSettingsTab } from './tabs/ExternalDeviceSettingsTab';
import { integrationTypeNames } from '@/lib/integrationTypes/integrationTypes';

interface DeviceEditProps {
  deviceId?: string;
  onSave?: () => void;
  hideNavigation?: boolean;
}

const DevicePage = ({
  deviceId: propDeviceId,
  onSave,
  hideNavigation = false,
}: DeviceEditProps) => {
  const { id: urlDeviceId } = useParams<{ id: string }>();
  const deviceId = propDeviceId || urlDeviceId || '';
  const deviceQuery = useQuery(FetchDeviceQuery({ deviceId }));
  const device = deviceQuery.data;
  const [activeTab, setActiveTab] = useQueryState('tab', parseAsString.withDefault('cameras'));

  if (deviceQuery.isLoading) {
    return (
      <div className="flex h-[calc(100vh-3rem)] items-center justify-center">
        <h1 className="font-sans text-2xl font-bold text-white">Device Loading</h1>
      </div>
    );
  }

  if (deviceQuery.isError) {
    if (deviceQuery.error instanceof AxiosError && deviceQuery.error.status === 404) {
      return (
        <div className="flex h-[calc(100vh-3rem)] flex-col items-center justify-center gap-6">
          <h1 className="font-sans text-2xl font-bold text-white">Device not found</h1>
          <h1 className="font-sans text-xl text-white">
            If you are trying to edit a V1 device, please use V1 admin portal
          </h1>
          <Link className={buttonVariants()} to="/devices">
            Go Home
          </Link>
        </div>
      );
    }

    return (
      <div className="flex h-[calc(100vh-3rem)] flex-col items-center justify-center gap-6">
        <h1 className="font-sans text-2xl font-bold text-white">Could not fetch device</h1>
        <Link className={buttonVariants()} to="/devices">
          Go Home
        </Link>
      </div>
    );
  }

  if (!device) {
    return <NotFoundPage />;
  }

  return (
    <DeviceFormProvider deviceId={device.id}>
      <PageTitle title={device.name || 'Device'} />
      <div className="relative">
        <div
          data-onboarding="device-edit-header"
          className={cn(
            'sticky z-20 flex items-center justify-between border-b bg-[#111] px-6 py-3',
            hideNavigation ? 'top-0 border-[#2E3835] p-2' : 'top-14'
          )}
        >
          {!hideNavigation ? (
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 flex-col items-center justify-center gap-y-1 rounded-md border border-muted/40">
                <HardDrive className="h-5 w-5" />
                <p className="text-xs font-bold">
                  {integrationTypeNames[device.integration.integrationType]}
                </p>
              </div>
              <div className="flex flex-col">
                <h1 className="text-lg font-semibold">{device.name || 'Unnamed device'}</h1>
                <p className="text-xs text-gray-300">
                  <span className="mr-4 font-mono">
                    {[device.integration.brand, device.integration.name]
                      .filter(Boolean)
                      .join('\u2022')}
                  </span>
                  {device.externalConnectionLimitedDto &&
                    `Using External Connection (${device.externalConnectionLimitedDto.name})`}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />{' '}
                <h2 className="text-lg font-semibold">{device.name || 'Unnamed device'}</h2>
              </div>
            </div>
          )}
          <div className="flex gap-4">
            {device.externalConnectionLimitedDto && (
              <Link
                data-onboarding="device-edit-connection"
                className={`min-w-24 ${buttonVariants({ variant: 'outline' })}`}
                to={`/connections/${device.externalConnectionLimitedDto?.id}`}
              >
                <ArrowUpLeftFromSquare /> View {device.externalConnectionLimitedDto.name}
              </Link>
            )}

            <div data-onboarding="device-edit-save">
              <DeviceSubmitButton
                deviceId={deviceId}
                updated={device?.updated}
                onSuccess={onSave}
              />
            </div>
          </div>
        </div>
        <div className="flex">
          <Tabs value={activeTab} onValueChange={setActiveTab} className={cn('flex-1 transition-all duration-300')}>
            <TabsList
              data-onboarding="device-edit-tabs"
              className="sticky top-[57px] z-[30] flex w-full rounded-none border-x-0 border-b border-t-0 bg-[#111]"
            >
              <TabsTrigger
                data-onboarding="device-edit-tab-cameras"
                className="flex-1 px-6 data-[state=active]:bg-[#111]"
                value="cameras"
              >
                Cameras and Hardware
              </TabsTrigger>
              <TabsTrigger
                data-onboarding="device-edit-tab-settings"
                className="flex-1 px-6 data-[state=active]:bg-[#111]"
                value="settings"
              >
                Settings
              </TabsTrigger>
              <TabsTrigger
                data-onboarding="device-edit-tab-validation"
                className="flex-1 px-6 data-[state=active]:bg-[#111]"
                value="external-device"
              >
                External Device Setup
              </TabsTrigger>
            </TabsList>

            <div data-onboarding="device-edit-content" className="flex-1">
              <TabsContent value="cameras" className="mx-auto max-w-screen-3xl">
                <CamerasTab
                  deviceId={device.id}
                  integrationType={device.integration.integrationType}
                  projectId={device.projectId}
                />
              </TabsContent>

              <TabsContent value="settings">
                <ConnectionsAndSettingsTab {...device} />
              </TabsContent>

              <TabsContent value="external-device">
                <ExternalDeviceSettingsTab {...device} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </DeviceFormProvider>
  );
};

export default DevicePage;
